import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Download, Upload } from "lucide-react";
import { api, errMsg } from "../lib/api";
import { UserImport } from "../components/UserImport";
import {
  PageHeader, DataTable, Td, FormDialog, Field, TextInput, SelectInput, ActionButton, StatusBadge,
} from "../components/common";

const ROLES = ["admin", "shop_manager", "dispatcher", "salesperson", "mechanic"];
const EMPTY = { name: "", email: "", password: "", role: "salesperson", hourly_rate: 0 };
const EMPTY_DRIVER = { driver_name: "", email: "", password: "" };

export default function UsersPage() {
  const [users, setUsers] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [driverOpen, setDriverOpen] = useState(false);
  const [driverForm, setDriverForm] = useState(EMPTY_DRIVER);
  const [driverNames, setDriverNames] = useState([]);
  const [editing, setEditing] = useState(null);
  const [editForm, setEditForm] = useState({ name: "", email: "", password: "", role: "" });
  const [busy, setBusy] = useState(false);
  const [importOpen, setImportOpen] = useState(false);

  const load = () => api.get("/users").then((r) => setUsers(r.data)).catch((e) => toast.error(errMsg(e)));

  const exportUsers = async () => {
    try {
      const { data } = await api.get("/users/export.csv", { responseType: "blob" });
      const url = URL.createObjectURL(data);
      const a = document.createElement("a");
      a.href = url; a.download = "users_export.csv";
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
      toast.success("Users CSV download started");
    } catch (e) {
      toast.error("Could not export users. Admin access required.");
    }
  };
  useEffect(() => {
    load();
    api.get("/assets").then((r) => {
      const names = [...new Set(r.data.map((a) => (a.driver_name || "").trim()).filter(Boolean))].sort((a, b) => a.localeCompare(b));
      setDriverNames(names);
    }).catch(() => {});
  }, []);

  const createUser = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/users", form);
      toast.success(`User ${form.name} created`);
      setOpen(false);
      setForm(EMPTY);
      load();
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  const createDriver = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/users", { name: driverForm.driver_name, email: driverForm.email, password: driverForm.password, role: "driver" });
      toast.success(`Driver login created for ${driverForm.driver_name}`);
      setDriverOpen(false);
      setDriverForm(EMPTY_DRIVER);
      load();
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  const openEdit = (u) => {
    setEditing(u);
    setEditForm({ name: u.name, email: u.email, password: "", role: u.role, hourly_rate: u.hourly_rate || 0 });
  };

  const saveEdit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = { name: editForm.name, email: editForm.email, role: editForm.role, hourly_rate: Number(editForm.hourly_rate || 0) };
      if (editForm.password) payload.password = editForm.password;
      await api.patch(`/users/${editing.id}`, payload);
      toast.success(`${editForm.name} updated${editForm.password ? " — password changed" : ""}`);
      setEditing(null);
      load();
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  const toggleActive = async (u) => {
    try {
      await api.patch(`/users/${u.id}`, { active: !u.active });
      toast.success(`${u.name} ${u.active ? "deactivated" : "activated"}`);
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  return (
    <div data-testid="users-page">
      <PageHeader title="Users & Roles" subtitle="Team members with access to this company's modules" testId="users-header">
        <ActionButton testId="export-users-button" variant="secondary" onClick={exportUsers}>
          <Download size={16} /> Export Users CSV
        </ActionButton>
        <ActionButton testId="import-users-button" variant="secondary" onClick={() => setImportOpen(true)}>
          <Upload size={16} /> Import Users
        </ActionButton>
        <ActionButton testId="create-driver-button" variant="secondary" onClick={() => setDriverOpen(true)}>
          <Plus size={16} /> Add Driver
        </ActionButton>
        <ActionButton testId="create-user-button" onClick={() => setOpen(true)}>
          <Plus size={16} /> Add User
        </ActionButton>
      </PageHeader>

      <DataTable
        testId="users-table"
        columns={["Name", "Email", "Role", "Status", "Actions"]}
        rows={users}
        renderRow={(u) => (
          <tr key={u.id} className="hover:bg-slate-800/30 transition-colors duration-150" data-testid={`user-row-${u.id}`}>
            <Td className="font-medium text-slate-100">{u.name}</Td>
            <Td className="text-slate-400">{u.email}</Td>
            <Td><StatusBadge status={u.role} /></Td>
            <Td><StatusBadge status={u.active ? "active" : "suspended"} /></Td>
            <Td>
              <div className="flex items-center gap-1">
                <button data-testid={`user-edit-${u.id}`} onClick={() => openEdit(u)} className="p-1.5 rounded text-slate-500 hover:text-amber-400 hover:bg-amber-500/10 transition-colors duration-200" title="Edit user">
                  <Pencil size={14} />
                </button>
                <ActionButton testId={`user-toggle-${u.id}`} variant="secondary" onClick={() => toggleActive(u)} className="!px-3 !py-1 text-xs">
                  {u.active ? "Deactivate" : "Activate"}
                </ActionButton>
              </div>
            </Td>
          </tr>
        )}
      />

      <FormDialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)} title={`Edit ${editing?.name || "User"}`} testId="edit-user-dialog">
        <form onSubmit={saveEdit} className="space-y-4">
          <Field label="Full name">
            <TextInput testId="edit-user-name-input" value={editForm.name} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} required />
          </Field>
          <Field label="Email">
            <TextInput testId="edit-user-email-input" type="email" value={editForm.email} onChange={(e) => setEditForm({ ...editForm, email: e.target.value })} required />
          </Field>
          <Field label="New password (leave blank to keep current)">
            <TextInput testId="edit-user-password-input" type="password" minLength={8} value={editForm.password} onChange={(e) => setEditForm({ ...editForm, password: e.target.value })} placeholder="••••••••" />
          </Field>
          <Field label="Hourly Labor Rate ($)"><TextInput testId="edit-user-hourly-rate-input" type="number" min="0" step="0.01" value={editForm.hourly_rate ?? 0} onChange={(e) => setEditForm({ ...editForm, hourly_rate: e.target.value })} /></Field>
          {editing?.role !== "driver" && (
            <Field label="Role">
              <SelectInput testId="edit-user-role-select" value={editForm.role} onChange={(e) => setEditForm({ ...editForm, role: e.target.value })} options={ROLES.map((r) => ({ value: r, label: r.replaceAll("_", " ").toUpperCase() }))} />
            </Field>
          )}
          <ActionButton testId="edit-user-submit-button" type="submit" disabled={busy} className="w-full justify-center">
            {busy ? "Saving…" : "Save Changes"}
          </ActionButton>
        </form>
      </FormDialog>

      <FormDialog open={driverOpen} onOpenChange={setDriverOpen} title="Add Driver Login" testId="create-driver-dialog">
        <form onSubmit={createDriver} className="space-y-4">
          <Field label="Driver (from Asset Register)">
            <SelectInput
              testId="driver-name-select"
              value={driverForm.driver_name}
              onChange={(e) => setDriverForm({ ...driverForm, driver_name: e.target.value })}
              required
              placeholder="Select driver"
              options={driverNames.map((n) => ({ value: n, label: n }))}
            />
          </Field>
          <Field label="Email">
            <TextInput testId="driver-email-input" type="email" value={driverForm.email} onChange={(e) => setDriverForm({ ...driverForm, email: e.target.value })} required />
          </Field>
          <Field label="Temporary password">
            <TextInput testId="driver-password-input" type="password" minLength={8} value={driverForm.password} onChange={(e) => setDriverForm({ ...driverForm, password: e.target.value })} required />
          </Field>
          <ActionButton testId="driver-submit-button" type="submit" disabled={busy} className="w-full justify-center">
            {busy ? "Creating…" : "Create Driver Login"}
          </ActionButton>
        </form>
      </FormDialog>

      <FormDialog open={open} onOpenChange={setOpen} title="Add Team Member" testId="create-user-dialog">
        <form onSubmit={createUser} className="space-y-4">
          <Field label="Full name">
            <TextInput testId="user-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required />
          </Field>
          <Field label="Email">
            <TextInput testId="user-email-input" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required />
          </Field>
          <Field label="Temporary password">
            <TextInput testId="user-password-input" type="password" minLength={8} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} required />
          </Field>
          <Field label="Role">
            <SelectInput testId="user-role-select" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} options={ROLES.map((r) => ({ value: r, label: r.replaceAll("_", " ").toUpperCase() }))} />
          </Field>
          <Field label="Hourly Labor Rate ($)"><TextInput testId="user-hourly-rate-input" type="number" min="0" step="0.01" value={form.hourly_rate} onChange={(e) => setForm({ ...form, hourly_rate: Number(e.target.value) })} /></Field>
          <ActionButton testId="user-submit-button" type="submit" disabled={busy} className="w-full justify-center">
            {busy ? "Creating…" : "Create User"}
          </ActionButton>
        </form>
      </FormDialog>

      <UserImport open={importOpen} onOpenChange={setImportOpen} onDone={load} />
    </div>
  );
}
